// WX JWT (token_employer, token_enterprise) management utility
// Mirrors WX logic: store token by using environment-specific keys
// across localStorage and sessionStorage for unified auth enabled accounts.

import { REACT_APP_MONOLITH_BASE_URL } from 'runtimeEnvVars';

import { domainMeta } from 'utils/domainMeta';

const BASE_KEYS = ['token_employer', 'token_enterprise'];

// Audience values for determining token type
const ENTERPRISE_AUDIENCES = ['EnterpriseIdentity', 'SuperUser'];

function stageSuffix() {
  return domainMeta.environment;
}

function getStageKeys() {
  return BASE_KEYS.map(key => `${key}_${stageSuffix()}`);
}

/**
 * Decodes a JWT and extracts the payload
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
function decodeJwtPayload(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to decode JWT', e);
    return null;
  }
}

/**
 * Determines the correct storage key based on JWT audience
 * @param {string} token - JWT token
 * @returns {string|null} Storage key name ('token_enterprise' or 'token_employer') or null
 */
function getTokenTypeFromJwt(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  const audience = payload.audience || payload.aud;
  if (ENTERPRISE_AUDIENCES.includes(audience)) {
    return 'token_enterprise';
  }
  return 'token_employer';
}

export function storeWxJwt(jwt) {
  if (!jwt) return;

  // Determine which key to use based on JWT audience
  const tokenType = getTokenTypeFromJwt(jwt);
  if (!tokenType) {
    // eslint-disable-next-line no-console
    console.warn('Could not determine token type from JWT');
    return;
  }

  // Build the stage-specific key
  const stageKey = `${tokenType}_${stageSuffix()}`;

  // Store in localStorage
  try {
    localStorage.setItem(stageKey, jwt);
  } catch (e) {
    // ignore storage errors (private mode, quota, etc.)
  }

  // Store in sessionStorage
  try {
    sessionStorage.setItem(stageKey, jwt);
  } catch (e) {
    // ignore
  }
}

export function clearWxJwt() {
  const stageKeys = getStageKeys();
  stageKeys.forEach(stageKey => {
    try {
      localStorage.removeItem(stageKey);
    } catch (e) {
      // ignore
    }
    try {
      sessionStorage.removeItem(stageKey);
    } catch (e) {
      // ignore
    }
  });
}

// JWT ready promise management
let jwtReadyPromise = null;
let jwtReadyResolve = null;
let isFetching = false;

// For testing purposes
export function resetJwtState() {
  jwtReadyPromise = null;
  jwtReadyResolve = null;
  isFetching = false;
}

// Initialize the ready promise
function initJwtReadyPromise() {
  if (!jwtReadyPromise) {
    jwtReadyPromise = new Promise(resolve => {
      jwtReadyResolve = resolve;
    });
  }
}

function deriveAccountSlugFromLocation() {
  try {
    const path = window.location.pathname.split('/').filter(Boolean);
    return path[0];
  } catch (e) {
    return null;
  }
}

function buildWxJwtUrl(whoami) {
  const slug =
    whoami?.account_slug ||
    whoami?.account_name ||
    deriveAccountSlugFromLocation();
  if (!slug) return null;
  return `${REACT_APP_MONOLITH_BASE_URL}/${slug}/integrations/wx_jwt`;
}

export function fetchAndStoreWxJwt(whoami) {
  // Initialize the ready promise if not already done
  initJwtReadyPromise();

  // If already fetching, return the existing promise
  if (isFetching) {
    return jwtReadyPromise;
  }

  // Set fetching flag
  isFetching = true;

  // Start the fetch asynchronously
  (async () => {
    try {
      const url = buildWxJwtUrl(whoami);
      if (!url) {
        // No URL means we can't fetch JWT, resolve as ready (not needed)
        jwtReadyResolve(false);
        return;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch WX JWT: ${response.status}`);
      }

      const data = await response.json();
      if (data?.jwt) {
        storeWxJwt(data.jwt);
        // JWT successfully stored, resolve as ready
        jwtReadyResolve(true);
        return;
      }

      // No JWT in response, resolve as ready (not available)
      jwtReadyResolve(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('#ba48cbc8 Failed to fetch and store WX JWT', e);
      // Error occurred, resolve as ready (failed)
      jwtReadyResolve(false);
    } finally {
      isFetching = false;
    }
  })();

  // Always return the ready promise
  return jwtReadyPromise;
}

export async function waitForWxJwtReady() {
  // Initialize the ready promise if not already done
  initJwtReadyPromise();

  // It will only ever resolve once JWT is fetched, otherwise wait indefinitely
  await jwtReadyPromise;
}

export function getWxJwt() {
  const stageKeys = getStageKeys();
  let token = null;

  // Try to find a token in any of the possible keys
  stageKeys.some(stageKey => {
    try {
      token =
        sessionStorage.getItem(stageKey) || localStorage.getItem(stageKey);
      return token;
    } catch (e) {
      // ignore
      return false;
    }
  });

  return token;
}

export function hasWxJwt() {
  return Boolean(getWxJwt());
}
